<template>
	<div v-if="isExtensionInstalled">
		<div class="display-flex flex-justify-space-between on-boarding-step-wrapper">
			<div class="display-flex flex-column">
				<div v-for="subStep in topSectionSubSteps" :key="subStep.id">
					<component
						v-on="$listeners"
						:is="subStep.component"
						:subStepDetails="subStep"
						:commandBarShortcut="userReadableShortcut"
						:class="{ 'not-active-sub-step': subStep.id !== activeStepId }"
						:commandBarExampleInstallableId="commandBarExampleInstallableId"
					/>
					<div class="section-divider" />
				</div>
			</div>
			<div class="image-container">
				<v-img :src="OnBoardingToolsImage" />
			</div>
		</div>
		<div v-for="(subStep, i) in bottomSectionSubSteps" :key="subStep.id">
			<component
				v-on="$listeners"
				:is="subStep.component"
				:subStepDetails="subStep"
				:commandBarShortcut="userReadableShortcut"
				:class="{ 'not-active-sub-step': subStep.id !== activeStepId }"
				:commandBarExampleInstallableId="commandBarExampleInstallableId"
			/>
			<div v-if="i + 1 !== bottomSectionSubSteps.length" class="section-divider" />
		</div>
	</div>
	<div v-else class="flex-centerize flex-column mb-9">
		This step requires Ply's extension. Please download and complete
		<AnemicButton class="chrome-store-button mt-2" data-cy="registration-username" @click="goToChromeStore">
			<SIcon name="chrome" class="mr-2 mt-1" />
			Go to the Chrome Store
		</AnemicButton>
	</div>
</template>

<script>
import { Modes } from '@/common.js';
import OnBoardingToolsImage from '@/assets/icons/on-boarding-tools.png';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import { PROD_COMMAND_BAR_INSTALLABLE_ID, STAGING_COMMAND_BAR_INSTALLABLE_ID } from '../../../../on-boarding-service.js';

export default {
	name: 'OnBoardingGetToKnowTools',
	props: { stepDetails: { type: Object } },
	components: {},
	computed: {
		isCompleted() {
			return this.stepDetails.isCompleted;
		},
		userReadableShortcut() {
			if (!this.commandBarShortcut) {
				return null;
			}
			const keyShortcuts = {
				'⇧': 'SHIFT + ',
				'⌘': 'CMD + ',
				'⌃': 'CTRL + ',
				'⌥': 'OPTION + ',
			};
			const unreadableShortcut = this.commandBarShortcut;
			return unreadableShortcut.replace(/⇧|⌘|⌥|⌃/gi, (matched) => keyShortcuts[matched]);
		},
		topSectionSubSteps() {
			return this.stepDetails.subSteps.filter((subStep) => !subStep.bottomSection);
		},
		bottomSectionSubSteps() {
			return this.stepDetails.subSteps.filter((subStep) => subStep.bottomSection);
		},
		activeStepId() {
			return this.stepDetails.subSteps.find((subStep) => !subStep.isCompleted)?.id;
		},
		commandBarExampleInstallableId() {
			if (process.env.VUE_APP_PLY_ENV === Modes.Production) {
				return PROD_COMMAND_BAR_INSTALLABLE_ID;
			} else {
				return STAGING_COMMAND_BAR_INSTALLABLE_ID;
			}
		},
	},
	methods: {
		goToChromeStore() {
			const { VUE_APP_CHROME_EXTENSION_APP_URL: chromeAppURL } = process.env;
			window.open(chromeAppURL, '_blank');
		},
		async checkIfExtensionInstalled() {
			const isInstalled = await ExtensionCommunicationService.isInstalled();
			this.isExtensionInstalled = isInstalled;
			if (isInstalled) {
				clearInterval(this.isExtensionInstalledInterval);
			}
		},
		async getCommandBarShortcutFromExtension() {
			const commandBarShortcut = await ExtensionCommunicationService.getCommandBarShortcut();
			this.commandBarShortcut = commandBarShortcut?.commandBarShortcut?.shortcut;
		},
		async setIsExtensionInstalled() {
			this.isExtensionInstalled = await ExtensionCommunicationService.isInstalled();
		},
	},
	async created() {
		await this.setIsExtensionInstalled();
		this.extensionInstalledSubscription = ExtensionCommunicationService.subscribeToExtensionInstalled(this.setIsExtensionInstalled);
		await this.getCommandBarShortcutFromExtension();
		this.commandBarShortcutInterval = setInterval(() => {
			this.getCommandBarShortcutFromExtension();
		}, 3000);
	},
	beforeDestroy() {
		this.commandBarShortcutInterval && clearInterval(this.commandBarShortcutInterval);
		ExtensionCommunicationService.unsubscribeToExtensionInstalled(this.extensionInstalledSubscription);
	},
	data() {
		return {
			OnBoardingToolsImage,
			commandBarShortcut: null,
			isExtensionInstalled: false,
			commandBarShortcutInterval: null,
			isExtensionInstalledInterval: null,
			extensionInstalledSubscription: null,
		};
	},
};
</script>

<style scoped lang="scss">
.image-container {
	width: 433px;
	height: 257px;
}
.section-divider {
	height: 50px;
}
.not-active-sub-step {
	opacity: 0.2;
	transition: 0.5s;
	&:hover {
		opacity: 0.6;
		transition: 0.5s;
	}
}
.chrome-store-button {
	height: 45px !important;
	font-size: 14px !important;
	color: var(--bg-color) !important;
	border: 1px solid var(--bg-color) !important;
	background: var(--bold-border-color) !important;
}
</style>
